/**
 * Content for the 5 dedicated SEO landing pages.
 * Titles, descriptions and canonicals are centralised in /lib/seo-pages.js
 * (single source of truth shared with the postbuild meta stamper).
 *
 * NOTE on casing: all visible page text (h1, h2, intros, list items, body
 * copy, faqs, ctas, pricing line) is written in lowercase to match the
 * homepage's lowercase aesthetic. SEO-only fields (title, description,
 * keywords, alt text) keep their natural capitalisation for search engines.
 */
import { SEO_PAGES, canonicalFor } from "./seo-pages";

const ORIGIN = "https://peanutfactorystudio.com";

export const SERVICE_PAGES = {
  production: {
    slug: "production",
    title: SEO_PAGES["/production"].title,
    description: SEO_PAGES["/production"].description,
    h1: "full-service photography & video production",
    eyebrow: "production · brief-to-delivery",
    pricingLine: "content-day packages from £750 · custom multi-day quotes on brief",
    keywords:
      "photography production London, video production London, full service photography studio London, end to end shoot production, campaign production Hackney Wick, moodboard to delivery photography",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/n94m4by1_unnamed%20%283%29.jpg",
    heroImageAlt:
      "Behind-the-scenes at a full production shoot — photographer, lighting stands, softbox and model on set at Peanut Factory Studio, Hackney Wick",
    quickAnswers: [
      "full-service photography and video production — we take your moodboard and turn it into finished, delivery-ready assets",
      "in-house team covers photographer, videographer, art director, stylist, mua, producer, assistants and post — bring your brief, we bring the crew",
      "based in a 600 sq ft daylight studio in hackney wick — plus location production across london and the uk when the shot needs it",
      "content-day packages from £750 · half-day, full-day and multi-day production rates · same-day quotes on every enquiry",
      "trusted by fashion, beauty, lifestyle, hospitality and dtc brands since 2016 — with a track record of hitting deadlines and briefs",
    ],
    intro:
      "peanut factory studio is a full-service photography and video production company based in hackney wick, east london. we take briefs from brands, agencies and founders — anything from a mood-board to a fully signed-off shot list — and produce them end-to-end. crew, kit, styling, art direction, hair and makeup, catering, post: we handle the full production so you don't have to manage six suppliers to get one campaign shot.",
    sections: [
      {
        h2: "what we mean by 'end-to-end production'",
        kind: "list",
        items: [
          "creative development — we help you build or refine the moodboard, shot list and treatment",
          "crew sourcing — photographer, videographer, art director, stylist, hair + mua, producer, assistants, catering",
          "casting + wardrobe — talent casting, wardrobe styling, prop sourcing and set dressing",
          "studio + location — shoot in our daylight studio, on location across london / the uk, or both",
          "on-shoot production — timings, call sheets, budget management, catering, client hospitality",
          "post-production — colour grading, retouching, video edit, motion, delivery in every spec you need",
          "delivery — assets sized and formatted for socials, digital, print, ooh and dooh, ready to go live",
        ],
      },
      {
        h2: "who we produce for",
        kind: "list",
        items: [
          "fashion + beauty brands — seasonal campaigns, lookbooks, ecom refreshes, launch capsules",
          "lifestyle + homeware — brand imagery, product launches, editorial content, still + motion",
          "hospitality + food + drink — menu photography, brand campaign imagery, launch content",
          "dtc + startups — brand launch imagery, hero campaign, seasonal content, founder portraits",
          "creative + marketing agencies — plug-in production for agency-led work (see /for-agencies)",
          "publications + editors — commissioned shoots, editorial productions, cover stories",
        ],
      },
      {
        h2: "we bring moodboards to life",
        kind: "prose",
        body:
          "most of our clients come to us with a moodboard — a folder of references, screenshots and pinterest boards — and a vague sense of the world they want to build. our job is to translate that into a real set, a real shoot day, a real batch of finished imagery. we spec the lighting to match the reference, source the props and wardrobe, cast talent that fits the aesthetic and direct the shoot so the finished frames look and feel like the moodboard, only better. no watering-down, no compromise — the daylight studio and in-house crew give us the flexibility to actually hit the brief.",
      },
      {
        h2: "content-day production for brands",
        kind: "prose",
        body:
          "one of our most popular production formats is the content day — a single shoot day at the studio that delivers a whole season's worth of finished assets for a brand's social, ecom and marketing channels. content-day packages start from £750 and include photographer, studio, light retouching and 15–40+ delivered images. we design the day around your brief — multiple set-ups, wardrobe changes, product rotations — and hand back a polished library within a week. for brands that need to keep the content pipeline full without running six separate shoots a quarter, it's the most efficient way to work.",
      },
      {
        h2: "campaign + editorial production",
        kind: "prose",
        body:
          "for hero campaign shoots, editorial productions and brand launches we run bigger multi-day productions with full creative direction, casting, styling and post. typical set-up: 1–3 days of shoot at the daylight studio and / or on location across london, with 8–20 crew including photographer, videographer, art director, stylist, mua, producer and assistants. we manage the schedule, the call sheets, the catering and the delivery so your team can focus on the client and the creative — not on chasing suppliers.",
      },
      {
        h2: "the peanut factory difference",
        kind: "list",
        items: [
          "a single point of contact — you brief us once, we handle every supplier",
          "the daylight studio is our home base — no rental scramble, no studio-availability roulette",
          "an in-house prop and furniture library — save on rental runs, faster set changes",
          "flat, transparent pricing — no hidden day-rate mark-ups, no surprise bills",
          "same-day quotes and fast turnaround — we know how agency + brand timelines work",
          "a decade of production experience since 2016 — the muscle memory to hit any brief",
        ],
      },
    ],
    faqs: [
      {
        q: "how does a full production shoot with peanut factory studio work?",
        a: "you send us your brief and moodboard, we come back within hours with a proposal that covers crew, kit, timings, delivery and cost. once signed off we handle every part of the production — sourcing crew, booking talent, styling wardrobe, prepping the studio or location, running the shoot day and delivering the finished assets. you get one point of contact from start to finish, not six.",
      },
      {
        q: "how much does a full production cost?",
        a: "content-day packages start from £750 for a single day at the studio with photographer and light retouching. bigger campaign shoots with full crew, casting, styling and post are priced on brief — typically £2.5k–£15k+ depending on scope, crew size, days shot and delivery specs. send us the brief and we'll come back with a tailored quote within hours.",
      },
      {
        q: "can you produce shoots outside your daylight studio?",
        a: "yes. we shoot in-studio and on location across london and the rest of the uk. a lot of campaigns work best as a hybrid — hero imagery in the daylight studio, environmental / lifestyle content on location — and we can plan, permit and produce both from a single production plan.",
      },
      {
        q: "who owns the imagery and usage rights?",
        a: "you do. we deliver all imagery with full commercial usage rights for use across your brand's owned, earned and paid channels. we retain the right to feature the work in our portfolio and social channels unless you specifically request otherwise — we're happy to sign nda / embargo agreements before shoot day.",
      },
      {
        q: "what's your typical turnaround for production work?",
        a: "5 business days for photography retouching, 7–10 for video edit, 48 hours for headshots. rush turnaround (24–48h) is available on request for a small surcharge — flag it in the brief and we'll build the schedule around it.",
      },
      {
        q: "can we bring our own crew and just book you for production management?",
        a: "yes — we're happy to plug into an existing crew or agency-led production and just handle the production management, studio, and any missing crew slots you need to fill. see /for-agencies for the agency-facing version of this.",
      },
      {
        q: "how quickly can you start a production?",
        a: "most productions kick off within 1–3 weeks of brief sign-off. faster is possible for smaller content-day shoots (as short as 3–5 days from brief to shoot day). peak seasons (feb–apr, sept–nov) fill up faster — get in touch as early as you can if you have a fixed campaign launch date.",
      },
    ],
    cta: { label: "brief us on your production", href: "/#contact" },
  },

  "studio-hire": {
    slug: "studio-hire",
    title: SEO_PAGES["/studio-hire"].title,
    description: SEO_PAGES["/studio-hire"].description,
    h1: "daylight photography studio hire — hackney wick",
    eyebrow: "studio hire",
    pricingLine: "from £50 per hour or £350 per day",
    keywords:
      "studio hire London, daylight studio Hackney Wick, photography studio east London, victorian studio London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/9103ab2r_PEANUT%20FACTORY%20STUDIO-10.jpg",
    heroImageAlt:
      "600 sq ft daylight photography studio with skylights, white walls and pink stairs at Peanut Factory, Hackney Wick",
    quickAnswers: [
      "600 sq ft natural daylight studio in hackney wick, east london (e3) — fish island, 5 min walk from hackney wick overground",
      "£50 per hour or £350 per day — free bowens lighting kit included with every full-day booking (10 hours)",
      "same-day quote on every enquiry, weekdays and weekends, 09:00–18:00",
      "set up for editorial, fashion, lookbook, e-commerce, brand campaign, music video, fashion film and headshot shoots",
      "props library, three coloramas, hand-painted backdrops, kitchen and makeup area — all on-site, all included",
    ],
    intro:
      "peanut factory studio is a 600 sq ft natural daylight photography studio for hire in hackney wick, east london — five minutes walk from hackney wick overground station, on fish island, postcode e3. set inside a grade ii listed victorian peanut factory, the space is built around 20+ original skylights — soft, diffused daylight that floods every corner of the studio. we've been welcoming photographers, brands, agencies and stylists since 2016. the space is set up ready to shoot, with a curated prop library, a wide range of coloramas and hand painted backdrops and a kitchen on-site.",
    sections: [
      {
        h2: "what's included with studio hire",
        kind: "list",
        items: [
          "600 sq ft daylight studio with 4m ceilings",
          "20+ original skylights — pure natural light all day, pink stairs, orange stairs, white walls and exposed brick details",
          "a wide range of colorama and hand painted backdrops",
          "curated prop library — folding chairs, tables, plants, vintage furniture",
          "kitchen with full appliances + makeup and changing area",
          "high-speed wi-fi, tethering points and changing rails",
          "free lighting kit (bowens + modifiers + c-stands + wireless trigger) with every full-day booking",
        ],
      },
      {
        h2: "day rate, hourly rate and what's included",
        kind: "prose",
        body:
          "studio hire starts from £50 per hour or £350 per day. full-day bookings (10 hours) include a free lighting kit — bowens heads with large soft box, 2 x c-stands, umbrellas and a wireless trigger — at no extra cost. for multi-day shoots, brand campaigns, fashion-week capsule shoots and editorial productions we offer custom rates. get in touch with your dates and brief and we'll send a tailored quote within hours.",
      },
      {
        h2: "how to book",
        kind: "prose",
        body:
          "we respond to every enquiry the same day. send us your dates, the kind of shoot you're producing and any special requirements (extra lighting, crew, props beyond what's on-site). we'll reply with a quote and lock the booking in once you confirm. most clients book 2–6 weeks in advance, but we can usually accommodate last-minute requests for short-notice shoots.",
      },
    ],
    faqs: [
      {
        q: "how big is the studio?",
        a: "the studio is 600 sq ft of clear shooting space, with 4m ceilings under the original victorian skylight roof. the floor plan is open with a few stylable corners — pink stairs at one end, exposed brickwork, white walls and a small kitchen / changing area off the main room. easily accommodates fashion shoots, lookbooks, brand campaigns, music videos and crewed productions of 8–15 people.",
      },
      {
        q: "is the studio fully daylit?",
        a: "yes — 20+ original sawtooth skylights run the length of the roof, providing soft, diffused natural light from sunrise to sunset throughout the year. the victorian roof was originally designed to flood the peanut factory floor with even, north-facing light, which is exactly the light photographers and stylists want for editorial, fashion and beauty work. on overcast days the light stays consistently soft; on bright days you'll get directional shafts.",
      },
      {
        q: "where exactly is the studio?",
        a: "we're on fish island in hackney wick, east london, postcode e3. five minutes' walk from hackney wick overground station (london overground line, direct trains from stratford, dalston, willesden junction and richmond), ten minutes from stratford international, and twenty minutes from liverpool street. fish island is in the queen elizabeth olympic park area, with plenty of cafes, supplier shops and stylist warehouses within walking distance.",
      },
      {
        q: "is there parking?",
        a: "limited street parking is available on-site for short-term loading and unloading of kit and wardrobe. fish island is a controlled parking zone monday to friday during business hours, so longer-stay parking is best done at the nearby olympic park public car parks or at one of the secure car parks in stratford. most of our clients arrive via hackney wick overground — it's the easiest way in.",
      },
      {
        q: "can you supply lighting equipment?",
        a: "yes — every full-day booking includes a free lighting kit as standard (bowens heads with a large softbox, two c-stands, umbrellas, weights and a wireless trigger). additional bowens flash packages, continuous blondes, modifiers, polyboards and colour gels are available to hire at the same time. for off-site productions we can also crate kit for collection. just let us know what you need at booking and we'll have it ready in the studio when you arrive.",
      },
      {
        q: "can we shoot video as well as stills?",
        a: "absolutely. the daylight space works equally well for stills and motion — we host fashion films, music videos, brand spots, talking-head interviews and product films regularly. the ceiling height (4m) accommodates wide-angle setups and full-body shots without compromise, and the kitchen / changing room offers an easy switch space for crew downtime. we can also bring in continuous lighting on request.",
      },
    ],
    cta: { label: "check availability", href: "/#contact" },
  },

  "photography-video": {
    slug: "photography-video",
    title: SEO_PAGES["/photography-video"].title,
    description: SEO_PAGES["/photography-video"].description,
    h1: "in-house photography & video production",
    eyebrow: "photography & video",
    pricingLine: "project rates available on enquiry",
    keywords:
      "photography studio London, video studio London, fashion photography Hackney Wick, brand photography London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/n94m4by1_unnamed%20%283%29.jpg",
    heroImageAlt:
      "Editorial fashion shoot in progress at Peanut Factory Studio, East London",
    intro:
      "beyond studio hire, peanut factory studio offers a full in-house production service — photography, videography, direction and crewed shoots — for editorial, fashion, e-commerce and brand campaigns. our team has been shooting for fashion brands, lifestyle publications, hotels, restaurants and creative agencies since 2016. we bring the gear, the eye and the daylight studio.",
    sections: [
      {
        h2: "what we shoot",
        kind: "list",
        items: [
          "editorial fashion — magazine spreads, lookbooks, campaign hero imagery",
          "e-commerce — clean product photography, on-model shots, flat-lay and lifestyle",
          "brand campaigns — stills + motion for digital, social and ooh",
          "lifestyle and hospitality — interiors, food, drinks, talent",
          "music videos and fashion films — on the studio's daylight set or on location",
          "headshots and corporate portraits — see our dedicated headshot service",
        ],
      },
      {
        h2: "how we work",
        kind: "prose",
        body:
          "we take briefs at any stage — from a rough mood board to a fully-produced shoot list. our process is fast: discovery call, treatment, scheduling, production, delivery. we can handle stylist sourcing, casting, mua booking, set styling and post-production end-to-end. or we'll happily plug into your existing team and just bring the shooter and the studio.",
      },
      {
        h2: "pricing",
        kind: "prose",
        body:
          "photography and video projects are quoted per shoot. half-day, full-day and multi-day rates available. send us your brief — concept, deliverables, timeline and any wardrobe or talent notes — and we'll come back with a tailored estimate within 24 hours.",
      },
    ],
    faqs: [
      {
        q: "do you shoot on location?",
        a: "yes — we shoot in-studio and on location across london and the rest of the uk.",
      },
      {
        q: "what's your typical turnaround?",
        a: "standard delivery is 5 business days for stills and 7–10 for video. rush turnaround available on request.",
      },
      {
        q: "do you provide retouching?",
        a: "light retouching is included on all photography projects. heavy retouching, beauty work and compositing are priced separately — see our retouching service.",
      },
      {
        q: "can you provide a full crew?",
        a: "yes — we book stylists, muas, art directors, producers and assistants on request. many of our crew are local to east london.",
      },
    ],
    cta: { label: "get a quote", href: "/#contact" },
  },

  headshots: {
    slug: "headshots",
    title:
      "Headshot Photography London | Peanut Factory Studio, Hackney Wick",
    description:
      "Affordable natural daylight headshot photography in East London. From £250 for a one-hour session with five fully-retouched final images. Actors, founders, creatives and corporate teams welcome.",
    h1: "affordable headshot photography in east london",
    eyebrow: "head shots",
    pricingLine: "from £250 — one-hour session, 5 finished images",
    keywords:
      "headshot photography London, headshots Hackney Wick, actor headshots London, corporate headshots east London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/u0molbw5_260401_FIFI_005web.jpg",
    heroImageAlt:
      "Natural daylight headshot session at Peanut Factory Studio, Hackney Wick",
    galleryEyebrow: "recent sessions",
    galleryHeadingPrefix: "recent",
    galleryHeadingAccent: "headshots",
    galleryGridClass: "grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4",
    galleryAspect: "aspect-square",
    gallery: [
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/3lmtn2sk_Headshots%201.jpg",
        alt: "Black and white editorial headshot — long blonde wavy hair, neutral expression — shot at Peanut Factory Studio, Hackney Wick",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/i6q4tl5v_Headshots%202.jpg",
        alt: "Colour studio headshot — blonde curly hair, warm smile, beaded necklace — shot at Peanut Factory Studio, Hackney Wick",
      },
    ],
    intro:
      "peanut factory studio offers natural daylight headshot photography for actors, founders, creatives and entire company teams. sessions run in our 600 sq ft hackney wick studio under pure natural light — no flat strobe shadow, no orange uplight, just clean, contemporary portraits. whether you need a fresh agent profile, a linkedin refresh or a corporate roll-out for your team, we've got you.",
    sections: [
      {
        h2: "what's included with a headshot session",
        kind: "list",
        items: [
          "one full hour with the studio to yourself",
          "two backdrop options (wide range of colours and hand painted backdrops to choose from)",
          "three outfit changes",
          "two photographers' eyes on shot selection",
          "five fully-retouched final images delivered within 48 hours",
          "web and print sizing for each final image",
        ],
      },
      {
        h2: "headshot pricing",
        kind: "prose",
        body:
          "sessions start from £250 for a one-hour booking with five finished images. larger team and corporate bookings are priced on enquiry. discounts apply for groups of five or more. headshots include light retouching as standard; heavier beauty work is priced separately.",
      },
      {
        h2: "how to book",
        kind: "prose",
        body:
          "email info@peanutfactorystudio.com with some dates that work for you and we'll lock in the next available slot. sessions run weekdays and weekends from 09:00–18:00.",
      },
    ],
    faqs: [
      {
        q: "do you do corporate team shoots?",
        a: "yes — we regularly photograph entire company teams in a single half-day at the studio in hackney wick. each person gets a 10–15 minute slot with two outfit options, and we'll deliver each person three to five retouched final images within 48 hours. teams of 8–12 people fit comfortably across a half-day; larger groups (15–30) we'd recommend a full day. discount applies for any team booking of five or more.",
      },
      {
        q: "can i bring a stylist?",
        a: "of course — many clients bring their own hair and makeup or styling team. if you'd rather we sort it, we work with several trusted hair and makeup artists local to hackney wick and east london who can come to the studio for a half-day or full-day rate. just let us know at booking and we'll send recommendations and quotes within hours.",
      },
      {
        q: "how long until i get my photos?",
        a: "48 hours for standard turnaround on all headshot sessions — you'll receive five fully-retouched final images delivered to your inbox in both web and print sizes. need them faster? same-day or next-morning delivery is available for an additional fee — just flag it when you book and we'll prioritise your edit.",
      },
    ],
    cta: { label: "book a session", href: "/#contact" },
  },

  equipment: {
    slug: "equipment",
    title: SEO_PAGES["/equipment"].title,
    description: SEO_PAGES["/equipment"].description,
    h1: "studio equipment hire — lighting, grip & modifiers",
    eyebrow: "equipment",
    pricingLine: "per-item rates available on enquiry",
    keywords:
      "studio equipment hire London, lighting kit hire Hackney Wick, Profoto hire London, grip hire east London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/gcdrlcpq_PHOTO-2026-03-05-12-14-43%20%283%29.jpg",
    heroImageAlt:
      "Professional studio lighting kit, c-stands and modifiers ready for hire at Peanut Factory Studio",
    intro:
      "whether you're hiring our daylight studio or need a kit-out for an off-site production, peanut factory studio rents professional lighting and grip equipment by the day. our gear list is curated for editorial and brand-campaign work, kept in good condition and ready to grab and go.",
    sections: [
      {
        h2: "what we hire",
        kind: "list",
        items: [
          "lighting — bowens flash packages and continuous blondes",
          "modifiers — large soft box, umbrellas, bounce boards",
          "grip — c-stands, boom arms, pumpkin weights, stage weights, a-clamps",
          "other — colour gels, neutral-density gels, batteries",
          "backdrop systems — colorama hangers, paper rolls, polyboards",
        ],
      },
      {
        h2: "free lighting kit with full-day studio bookings",
        kind: "prose",
        body:
          "every full-day studio hire booking (10 hours) includes one of our standard lighting kits — bowens heads, large soft box, umbrellas, 2 c-stands, weights and a wireless trigger — at no extra cost. additional lighting kits can be added for an additional fee.",
      },
      {
        h2: "pricing",
        kind: "prose",
        body:
          "equipment is rented per item per day. multi-day discounts apply for shoots of 3+ days. poa for large kit-outs and off-site productions. we also pair equipment hire with crewed photography and assistant bookings — get in touch and we'll put together a quote covering everything you need.",
      },
    ],
    faqs: [
      {
        q: "can i hire equipment without booking the studio?",
        a: "yes — equipment-only bookings are welcome. pickup is from fish island, hackney wick.",
      },
      {
        q: "do you deliver?",
        a: "local delivery within e1–e3 by arrangement. standard pickup from the studio.",
      },
      {
        q: "is everything insured?",
        a: "all gear is owned and maintained by us. hirers are responsible for damage during the booking period.",
      },
      {
        q: "do you provide an assistant or photographer?",
        a: "available on request — book through our photography service and we'll bundle equipment, crew and studio time into one quote.",
      },
    ],
    cta: { label: "get equipment quote", href: "/#contact" },
  },

  retouching: {
    slug: "retouching",
    title: SEO_PAGES["/retouching"].title,
    description: SEO_PAGES["/retouching"].description,
    h1: "retouching & colour grading",
    eyebrow: "retouching",
    pricingLine: "per-image and per-project rates on enquiry",
    keywords:
      "photo retouching London, fashion retouching, beauty retouching London, colour grading London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/yz9mpy04_unnamed%20%284%29.jpg",
    heroImageAlt:
      "Editorial retouching and colour grading work at Peanut Factory Studio, London",
    intro:
      "peanut factory studio's in-house retouching team delivers high-end post-production for editorial, fashion, e-commerce and beauty work. whether you've shot in our studio or with another team, we'll match your reference look, clean up the imperfections and finish your campaign in the colour and tone your brand demands.",
    sections: [
      {
        h2: "what we retouch",
        kind: "list",
        items: [
          "skin and beauty work — frequency separation, dodge & burn, hair clean-up, eye work",
          "fashion editorial — magazine-standard retouching, liquify, garment cleanup, colour grading",
          "e-commerce — flat lays, ghost-mannequin, white-background",
          "compositing — green screen, stitching, multi-shot composites",
          "headshot retouching — included with our headshot package",
        ],
      },
      {
        h2: "how it works",
        kind: "prose",
        body:
          "send us your selects, your reference look (or moodboard) and any specific notes on what to keep and what to clean. standard turnaround is 5 business days for editorial, 48 hours for headshots and 3 business days for e-commerce. two rounds of revisions are included as standard.",
      },
      {
        h2: "pricing",
        kind: "prose",
        body:
          "per-image and per-project rates. headshot retouching is included in our headshot package pricing. editorial and beauty retouching is priced on enquiry depending on complexity. volume e-commerce work poa — we offer competitive bulk rates for ongoing brand work.",
      },
    ],
    faqs: [
      {
        q: "do i need to have shot at your studio?",
        a: "no — we retouch work shot anywhere. just send us your raw files and reference and we'll handle the rest.",
      },
      {
        q: "what file formats do you accept?",
        a: "raw (preferred), tiff or full-resolution jpeg. final delivery in any format you need — jpeg, tiff, web-optimised etc.",
      },
      {
        q: "how many revisions are included?",
        a: "two rounds of revisions are included as standard. additional rounds priced on enquiry.",
      },
      {
        q: "do you provide colour grading for video?",
        a: "yes — davinci resolve and premiere pro supported. get in touch with your project specs and we'll quote.",
      },
    ],
    cta: { label: "send us your brief", href: "/#contact" },
  },

  "props-and-furniture": {
    slug: "props-and-furniture",
    title: SEO_PAGES["/props-and-furniture"].title,
    description: SEO_PAGES["/props-and-furniture"].description,
    h1: "props & furniture — available on-site",
    eyebrow: "props + furniture",
    pricingLine: "included free with every studio hire booking",
    keywords:
      "studio props london, furniture hire studio london, prop hire hackney wick, vintage furniture studio london",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/tgah3tkb_PROPS%2BFURNITURE025.jpg",
    heroImageAlt:
      "Blue corduroy cantilever chair with chrome frame — available on-site at Peanut Factory Studio, Hackney Wick",
    gallery: [
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/afz0fhja_PROPS%2BFURNITURE034.jpg",
        alt: "Studio props and furniture available for shoots at Peanut Factory Studio — image 1",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/qx7u49ta_PROPS%2BFURNITURE035.jpg",
        alt: "Studio props and furniture available for shoots at Peanut Factory Studio — image 2",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/xsffx8cs_PROPS%2BFURNITURE036.jpg",
        alt: "Studio props and furniture available for shoots at Peanut Factory Studio — image 3",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/fk8q5o77_PROPS%2BFURNITURE037.jpg",
        alt: "Studio props and furniture available for shoots at Peanut Factory Studio — image 4",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/k99dhlc4_PROPS%2BFURNITURE038.jpg",
        alt: "Studio props and furniture available for shoots at Peanut Factory Studio — image 5",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/tahz6zit_PROPS%2BFURNITURE029.jpg",
        alt: "Pink velvet bar stool with chrome base — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/8is6hzs7_PROPS%2BFURNITURE030.jpg",
        alt: "Pair of orange velvet bar stools — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/oaile5py_PROPS%2BFURNITURE031.jpg",
        alt: "Pair of vintage chrome and orange vinyl bar stools — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/6c8qswvi_PROPS%2BFURNITURE032.jpg",
        alt: "Pair of red plastic bar stools with black metal frame — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/kdrmvlej_PROPS%2BFURNITURE033.jpg",
        alt: "Vintage wood and cream upholstered side chair — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/nx5cvtnl_PROPS%2BFURNITURE027.jpg",
        alt: "Studio prop chair on warm wood backdrop — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/mylrc50l_PROPS%2BFURNITURE026.jpg",
        alt: "Vintage wood armchair with tweed cushion and patterned heart pillow — available on-site at Peanut Factory Studio",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/q2kkkqxj_PROPS%2BFURNITURE028.jpg",
        alt: "Vintage brown swivel office chair on castors — available on-site at Peanut Factory Studio",
      },
    ],
    intro:
      "peanut factory studio comes with a curated on-site library of props and furniture, included free with every studio hire booking. from folding chairs and vintage side tables to lamps, plants and stylable decor — everything is on-hand to help dress your set without an extra rental run.",
    sections: [
      {
        h2: "what's in the collection",
        kind: "list",
        items: [
          "folding chairs (10+) and bar stools",
          "vintage side tables, coffee tables and a couple of larger trestles",
          "lamps, candles and small lighting accessories",
          "a rotating selection of large plants and dried botanicals",
          "mirrors and frames",
          "stylable smaller props — books, ceramics, glassware",
        ],
      },
      {
        h2: "how it works",
        kind: "prose",
        body:
          "all props and furniture in the on-site collection are included free with your studio booking — no extra fee, no per-item charge. we'll happily reset between setups, and if there's something specific in our collection you'd like ready for your shoot, just mention it when you book and we'll have it staged.",
      },
      {
        h2: "need something we don't have?",
        kind: "prose",
        body:
          "if your shoot calls for specific furniture or props we don't carry, we can usually source it for you at cost via our local supplier network in east london. just let us know what you need at the time of booking.",
      },
    ],
    faqs: [
      {
        q: "is there an extra charge to use the props?",
        a: "no — everything in the on-site collection is included with your studio hire at no extra cost.",
      },
      {
        q: "can i bring my own props and styling?",
        a: "absolutely. most clients use a mix of our on-site collection and their own pieces. we have plenty of room to store extra kit during your shoot.",
      },
      {
        q: "do you offer prop sourcing?",
        a: "yes — if you need specific items we don't have, we can source via our local east london supplier network at cost.",
      },
      {
        q: "are the props well maintained?",
        a: "yes. the collection is curated and refreshed regularly. if anything is damaged or worn out we replace it.",
      },
    ],
    cta: { label: "book the studio", href: "/#contact" },
  },

  "studio-hire-london": {
    slug: "studio-hire-london",
    hidden: true,
    title: SEO_PAGES["/studio-hire-london"].title,
    description: SEO_PAGES["/studio-hire-london"].description,
    h1: "photography studio hire — london",
    eyebrow: "studio hire · london",
    pricingLine: "from £50 per hour or £350 per day",
    keywords:
      "studio hire London, photography studio London, daylight studio London, photo studio London, video studio London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/9103ab2r_PEANUT%20FACTORY%20STUDIO-10.jpg",
    heroImageAlt:
      "Daylight photography studio for hire in London — 600 sqft skylit space at Peanut Factory, Hackney Wick",
    quickAnswers: [
      "600 sq ft natural daylight photography studio for hire in london — east london, 5 mins from hackney wick overground",
      "£50 per hour or £350 per day — flexible half-day, full-day and multi-day rates available seven days a week",
      "20+ original victorian skylights, three coloramas, hand painted backdrops, props and a full kitchen — all on-site",
      "free bowens lighting kit included with every full-day booking (10 hours) — save £150+ on kit hire",
      "same-day quote on every enquiry, direct trains from stratford, dalston, liverpool street and central london",
    ],
    intro:
      "if you're searching for a photography studio to hire in london, peanut factory studio is a 600 sq ft natural daylight space in hackney wick, east london — designed for editorial fashion, brand campaigns, e-commerce, lookbooks, music videos and headshot productions. we've hosted photographers, agencies and brands since 2016 and are one of the most well-lit, prop-ready and easy-to-access daylight studios anywhere in london.",
    sections: [
      {
        h2: "why choose a london daylight studio",
        kind: "prose",
        body:
          "most london studios are basement or infill spaces that rely entirely on artificial light. peanut factory studio is different — the space sits under 20+ original victorian sawtooth skylights that flood the studio with even, north-facing natural light from sunrise to sunset. for editorial, fashion, beauty and lifestyle work the difference is enormous: softer skin tones, cleaner shadows, more flattering fabric drape and a look that reads as premium in-camera. no plug-in-and-hope, no fighting fluorescents — just the light photographers and stylists actually want.",
      },
      {
        h2: "what's included with every london studio hire",
        kind: "list",
        items: [
          "600 sq ft of clear shooting space with 4m ceilings",
          "20+ original skylights, exposed brick, pink stairs and white walls",
          "three coloramas and a wide range of hand painted backdrops",
          "curated prop library — folding chairs, tables, plants, vintage furniture",
          "kitchen with full appliances + makeup and changing area",
          "high-speed wi-fi, tethering points and changing rails",
          "free bowens lighting kit with every full-day booking (10 hours)",
        ],
      },
      {
        h2: "how to get to the studio from central london",
        kind: "prose",
        body:
          "the studio is on fish island, hackney wick, postcode e3. from central london the easiest route is the london overground line to hackney wick — it's a 5 minute walk to the studio door. direct trains run from stratford (5 min), dalston kingsland (10 min), highbury & islington (15 min) and richmond, willesden junction and clapham junction on the same overground line. from liverpool street or shoreditch you're 20 minutes door-to-door. limited on-site parking is available for loading — most productions arrive by train.",
      },
      {
        h2: "london studio day rate & hourly rate",
        kind: "prose",
        body:
          "studio hire starts from £50 per hour or £350 per day — genuinely one of the best-value daylight studios in london. full-day bookings (10 hours) include a free bowens lighting kit worth £150+, no extra charge. for multi-day shoots, brand campaigns and fashion-week productions we offer custom multi-day rates. get in touch with your dates and brief and we'll send a tailored london studio quote within hours.",
      },
    ],
    faqs: [
      {
        q: "where in london is the studio?",
        a: "we're on fish island, hackney wick, in the queen elizabeth olympic park area of east london — postcode e3. five minutes' walk from hackney wick overground, ten minutes from stratford international, and twenty minutes from liverpool street. it's the fastest-growing creative district in london and one of the easiest studio locations to reach from anywhere in the city.",
      },
      {
        q: "how does the london overground make it easy to get to?",
        a: "the london overground puts the studio one train ride from most of london. direct trains run from stratford, dalston kingsland, highbury & islington, willesden junction, richmond and clapham junction — no changes required. from central london you're 20–30 minutes door-to-door via the elizabeth line or central line to stratford, then a short walk or one stop on the overground.",
      },
      {
        q: "is peanut factory studio the biggest daylight studio in london?",
        a: "we're one of the largest single-room daylight studios in east london at 600 sq ft under a single continuous skylight roof. there are larger multi-room facilities in london, but for productions that want a single big daylit shooting space with no pillars, no ceiling grid and no artificial supplementation required, we're one of the best.",
      },
      {
        q: "can we do a full-crew brand shoot at your london studio?",
        a: "yes — the studio comfortably fits crews of 8–15 people including photographer, stylist, mua, art director, producer, assistants and talent. we regularly host brand-campaign productions with 12+ crew, and the kitchen and changing area gives your team space to work off-set. bigger productions (20+) work too — we'd just recommend planning kit and catering logistics with us in advance.",
      },
      {
        q: "how much notice do i need to book a london studio?",
        a: "most clients book 2–6 weeks in advance, but we can often accommodate last-minute bookings for short-notice shoots. if your date is flexible, we can usually offer a slot within the week. for peak fashion-week and campaign season (feb–apr, sept–nov) we recommend booking 6+ weeks ahead.",
      },
      {
        q: "do you offer half-day london studio hire?",
        a: "yes — half-day bookings are available at £250 for 5 hours. useful for smaller shoots, headshot sessions and single-look editorial. half-day bookings don't include the free lighting kit, but any equipment can be added at hire rate.",
      },
    ],
    cta: { label: "check availability", href: "/#contact" },
  },

  "headshots-london": {
    slug: "headshots-london",
    hidden: true,
    title: SEO_PAGES["/headshots-london"].title,
    description: SEO_PAGES["/headshots-london"].description,
    h1: "professional headshots — london",
    eyebrow: "headshots · london",
    pricingLine: "from £250 — one-hour session, 5 finished images",
    keywords:
      "headshots London, professional headshots London, corporate headshots London, actor headshots London, linkedin headshots London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/u0molbw5_260401_FIFI_005web.jpg",
    heroImageAlt:
      "Professional headshot session in natural daylight at Peanut Factory Studio, London",
    quickAnswers: [
      "professional headshot photography in london — natural daylight studio in hackney wick, east london",
      "sessions from £250 for one hour with five fully-retouched final images delivered within 48 hours",
      "actor headshots, corporate headshots, linkedin refreshes, agent profiles and full team roll-outs",
      "5 min from hackney wick overground — direct trains from stratford, dalston, central and west london",
      "team and corporate group bookings from 5+ people, priced on enquiry with volume discounts available",
    ],
    intro:
      "peanut factory studio offers professional headshot photography in london — natural daylight sessions in our hackney wick studio, five minutes from the overground. whether you need a fresh linkedin refresh, an actor's agent profile, a founder portrait or a corporate roll-out for your entire company team, we shoot clean, contemporary headshots that look premium without the corporate-cliché flat-strobe look most london headshot studios still produce.",
    sections: [
      {
        h2: "what makes our london headshot sessions different",
        kind: "prose",
        body:
          "most london headshot studios rely on artificial studio strobes bounced off white walls — the result is a flat, evenly-lit portrait that looks the same as every other headshot in your industry. we shoot under 20+ original victorian skylights, giving you soft, directional natural light with real dimension. the result is a portrait that reads as editorial rather than corporate — the difference between a photo that looks like a headshot, and one that looks like a real person.",
      },
      {
        h2: "what's included with a london headshot session",
        kind: "list",
        items: [
          "one full hour with the studio to yourself",
          "two backdrop options (wide range of colours and hand painted backdrops)",
          "three outfit changes",
          "two photographers' eyes on shot selection",
          "five fully-retouched final images delivered within 48 hours",
          "web and print sizing for each final image",
          "on-site changing / makeup area",
        ],
      },
      {
        h2: "who our london headshot clients are",
        kind: "list",
        items: [
          "actors — refreshing agent profiles for spotlight, mandy, casting websites",
          "founders + executives — polished portraits for press, pitch decks, board bios",
          "creatives + freelancers — portraits for portfolio sites, agency listings",
          "corporate teams — full team roll-outs, careers pages, linkedin refreshes",
          "students + graduates — clean linkedin headshots for job hunting",
          "authors + speakers — press portraits, book jackets, conference bios",
        ],
      },
      {
        h2: "corporate team headshots in london",
        kind: "prose",
        body:
          "we regularly photograph entire london company teams in a single half-day at the studio. each person gets a 10–15 minute slot with two outfit options, and we deliver 3–5 fully-retouched final images per person within 48 hours. teams of 8–12 fit comfortably in a half-day; larger teams (15–30) work best as a full day. we're set up for volume — clean workflow, tethered preview, on-site changing — and the daylight studio means everyone ends up looking like the best version of themselves. discount applies for team bookings of five or more.",
      },
      {
        h2: "getting to the london headshot studio",
        kind: "prose",
        body:
          "we're 5 minutes' walk from hackney wick overground station. direct trains run from stratford (5 min), dalston kingsland, highbury & islington, willesden junction and richmond. from central london you're 20 minutes door-to-door via the elizabeth line to stratford. the studio is on fish island, e3 — plenty of coffee shops for pre-shoot prep, and a changing / makeup area on-site.",
      },
    ],
    faqs: [
      {
        q: "how much do london headshots cost at your studio?",
        a: "individual headshot sessions start from £250 for a one-hour booking with five fully-retouched final images delivered within 48 hours. team and corporate roll-outs are priced on enquiry with volume discounts for groups of five or more.",
      },
      {
        q: "how quickly will i get my london headshots back?",
        a: "48 hours is standard turnaround for headshot sessions — you'll receive five fully-retouched final images to your inbox in both web and print sizes. same-day or next-morning delivery is available for an additional fee if you need them urgently for an audition, press deadline or job application.",
      },
      {
        q: "do you offer team headshot bookings in london?",
        a: "yes — team and corporate headshot bookings are one of our most popular services. we've photographed entire london company teams from 5-person startups to 40+ person agencies, delivering consistent, editorial-quality headshots for every team member. discount applies for any team of five or more.",
      },
      {
        q: "can i bring my own hair and makeup?",
        a: "absolutely — many clients bring their own hair and makeup or styling team. if you'd rather we sort it, we work with several trusted hair and makeup artists local to east london who can come to the studio for a half-day or full-day rate.",
      },
      {
        q: "where in london do i need to travel to?",
        a: "hackney wick, east london — 5 minutes' walk from hackney wick overground station. postcode e3. direct trains from stratford, dalston, central and west london on the overground line, or 20 minutes from liverpool street.",
      },
      {
        q: "can i see examples of your london headshot work?",
        a: "yes — see our headshots service page for recent session examples. we also have a full portfolio available on request if you'd like to see specific styles (corporate, editorial, actor, creative) that match what you're looking for.",
      },
    ],
    cta: { label: "book a headshot session", href: "/#contact" },
  },

  "for-brands": {
    slug: "for-brands",
    hidden: true,
    title: SEO_PAGES["/for-brands"].title,
    description: SEO_PAGES["/for-brands"].description,
    h1: "a photography studio built for brands",
    eyebrow: "for brands",
    pricingLine: "brand day rates and content-day packages on enquiry",
    keywords:
      "brand photography studio London, brand campaign photography, ecommerce studio London, product photography studio London, content day studio",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/n94m4by1_unnamed%20%283%29.jpg",
    heroImageAlt:
      "Editorial brand campaign shoot in progress at Peanut Factory Studio, East London",
    quickAnswers: [
      "a 600 sq ft daylight photography studio in east london — built for brand campaigns, e-commerce, product shoots and content days",
      "in-house team can handle direction, photography, styling, mua and post — or plug straight into your existing production",
      "£350 per day includes free lighting kit; content-day packages start from £750 with photographer and light retouching bundled",
      "trusted by fashion, beauty, lifestyle, hospitality and dtc brands since 2016 — flexible with brand guidelines and rush timelines",
      "everything under one roof: studio, kit, props, kitchen, changing area, retouching — save time and coordinate less",
    ],
    intro:
      "if you're a brand looking to shoot a campaign, refresh your e-commerce library, produce a season of social content or run a full content day — peanut factory studio is built for you. we've been the studio partner for fashion, beauty, lifestyle, hospitality and dtc brands since 2016. bring your creative team or use ours. bring your kit or use our free lighting kit. bring a full production crew or work with us end-to-end.",
    sections: [
      {
        h2: "what brands shoot at peanut factory studio",
        kind: "list",
        items: [
          "brand campaign hero imagery — stills + motion for digital, print and ooh",
          "e-commerce content — clean product shots, on-model, flat-lay, lifestyle",
          "seasonal lookbooks + capsule launches for fashion + beauty brands",
          "product launches and packshots for dtc, beauty, homeware, food + drink",
          "social content days — a full season's worth of instagram, tiktok, reels in one shoot",
          "brand video, music video, brand documentary and founder interview films",
        ],
      },
      {
        h2: "the two ways brands work with us",
        kind: "subgroups",
        subgroups: [
          {
            h3: "studio hire only",
            body:
              "book the daylight studio for a half-day, full-day or multi-day shoot and bring your own production team. you get the 600 sq ft space, 20+ skylights, three coloramas, hand painted backdrops, prop library, kitchen and changing area. every full-day booking includes a free bowens lighting kit. from £350 per day.",
          },
          {
            h3: "full production",
            body:
              "we handle everything — brief-to-delivery. our in-house team includes photographers, videographers, stylists, mua bookers, art directors, producers and retouchers. we take briefs at any stage from a rough mood board to a fully-produced shoot list. content-day packages start from £750 with photographer, studio, light retouching and 15+ delivered images.",
          },
        ],
      },
      {
        h2: "why a daylight studio matters for your brand",
        kind: "prose",
        body:
          "the light your product is shot in is the light your customer sees it in. flat artificial light flattens texture, kills fabric drape and makes skin tones look plasticky — the difference between an editorial-quality campaign image and a generic ecomm product shot. our 20+ original victorian skylights give your brand imagery the natural, directional light that makes products look premium, food look fresh, fabric look luxe and people look human. it's the reason our returning brands come back season after season.",
      },
      {
        h2: "content days for brands",
        kind: "prose",
        body:
          "planning a season of social content, an ecommerce refresh or a launch capsule? our content-day packages are built for high-volume shoot days. we deliver 15–40+ finished images from a single shoot day at the studio, with photographer, stylist and light retouching bundled in from £750. multi-day content sprints (2–5 days) are available at reduced day rates. get in touch with a brief and we'll come back with a tailored quote.",
      },
    ],
    faqs: [
      {
        q: "who owns the images and rights?",
        a: "you do. we deliver all imagery with full commercial usage rights for use across your brand's owned, earned and paid channels. we retain the right to use imagery in our portfolio and social channels unless you specifically request otherwise — happy to sign a nda or embargo images until a launch date if needed.",
      },
      {
        q: "what's your typical turnaround for brand work?",
        a: "5 business days for photography, 7–10 for video, 48 hours for headshots. rush turnaround (24–48h) is available on request for a small surcharge — flag it when you brief us and we'll build the schedule around it.",
      },
      {
        q: "can you shoot on location as well as in-studio?",
        a: "yes — we shoot in-studio and on location across london and the rest of the uk. a lot of brand campaigns work best as a hybrid: hero campaign images in the daylight studio, environmental / on-location content shot separately. we can handle both from a single production plan.",
      },
      {
        q: "do you work with pr and marketing agencies?",
        a: "yes — see our for-agencies page. we're set up to plug into an existing agency-led production or run the shoot end-to-end. either way we're flexible with brand guidelines, shot lists, retouching briefs and rush timelines.",
      },
      {
        q: "can we hold the studio exclusively for a brand campaign?",
        a: "yes — the studio is single-hire (we don't run parallel shoots), so every booking is exclusive by default. no shared space, no other clients coming through the door during your shoot day.",
      },
    ],
    cta: { label: "brief us on your campaign", href: "/#contact" },
  },

  "for-agencies": {
    slug: "for-agencies",
    hidden: true,
    title: SEO_PAGES["/for-agencies"].title,
    description: SEO_PAGES["/for-agencies"].description,
    h1: "a photography studio built for agencies",
    eyebrow: "for agencies",
    pricingLine: "agency day rates and multi-day production rates on enquiry",
    keywords:
      "photography studio for agencies London, agency production studio, creative agency studio hire, marketing agency photography London, content production studio London",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/gcdrlcpq_PHOTO-2026-03-05-12-14-43%20%283%29.jpg",
    heroImageAlt:
      "Agency-led production shoot at Peanut Factory Studio, East London — daylight photography studio for creative agencies",
    quickAnswers: [
      "an east london daylight photography studio built for creative, marketing and content agencies",
      "single-hire studio (no shared space), reliable schedule, same-day quotes and flexible multi-day rates",
      "in-house crew available if you need it — photographers, stylists, muas, producers, retouchers on-call",
      "£350 per day includes free lighting kit; multi-day agency rates and net-30 payment terms available",
      "trusted by creative agencies, marketing agencies, pr firms and content studios in london since 2016",
    ],
    intro:
      "peanut factory studio partners with london's creative and marketing agencies on brand campaigns, editorial productions and social content shoots. we're the studio agencies choose when they need reliability, flexibility, a daylight space that photographs well and a partner who can plug into an existing production without ego. bring your own crew or use ours. bring your brief or workshop it with us. we've been doing this since 2016 and it shows.",
    sections: [
      {
        h2: "why agencies book peanut factory studio",
        kind: "list",
        items: [
          "single-hire studio — no shared space, no parallel shoots, no client crossover",
          "reliable schedule — we don't overbook, we don't cancel and we respond same-day",
          "flexible with your production — bring your crew or use ours, we plug in either way",
          "daylight space that photographs premium — reduces post work, tightens delivery timelines",
          "prop library, three coloramas, hand painted backdrops and kitchen — save on rental runs",
          "multi-day production rates and net-30 payment terms available for established agency clients",
        ],
      },
      {
        h2: "how agencies typically work with us",
        kind: "subgroups",
        subgroups: [
          {
            h3: "studio-only bookings",
            body:
              "your agency runs the production, you bring your photographer, crew and talent — we provide the daylight studio, prop library and free lighting kit. from £350 per day. multi-day production rates available for shoots of 3+ days.",
          },
          {
            h3: "studio + in-house crew",
            body:
              "we plug in as photographer, videographer, stylist, mua booker or producer — whatever gap you need to fill in your existing production. useful for last-minute crew changes, additional units on multi-day shoots, or when your usual photographer isn't available.",
          },
          {
            h3: "end-to-end production",
            body:
              "we handle the full production for you — brief-to-delivery — while your agency stays client-facing. useful for smaller / faster projects, content-day sprints, or when you want to offload the production management entirely.",
          },
        ],
      },
      {
        h2: "reliability + flexibility for agency production",
        kind: "prose",
        body:
          "agency production runs on tight timelines, rotating creatives and clients who change their minds two days before shoot day. we've built the studio around that reality. same-day quotes on every enquiry. no overbooking. changing rooms and a kitchen so your talent isn't stuck in a corridor. easy in-out access for kit and wardrobe. and if a client throws a curveball 48 hours before shoot day, we'll usually be able to accommodate — a rescheduled day, an added lighting kit, an extra hour. call us and we'll figure it out.",
      },
      {
        h2: "multi-day and retainer arrangements",
        kind: "prose",
        body:
          "for agencies with repeat brand shoots across the year, we offer multi-day production rates, retainer arrangements and priority booking slots. talk to us about your annual content plan and we'll put together a preferred-agency rate that makes forecasting easier. net-30 payment terms available for established agency clients.",
      },
    ],
    faqs: [
      {
        q: "do you offer trade / agency rates?",
        a: "yes — we offer preferred rates for agencies with multi-day or recurring bookings. talk to us about your annual production plan and we'll put together a rate card that works for both of us.",
      },
      {
        q: "can you invoice with net-30 or net-60 terms?",
        a: "yes for established agency clients — net-30 payment terms are available on request for repeat clients. first-time bookings are payable on booking, standard practice for studio hire.",
      },
      {
        q: "can we hold the studio on option before confirming?",
        a: "yes — we hold provisional slots for agencies at no cost for up to 5 working days, longer with a small deposit. useful when you're waiting on client sign-off before locking a shoot date.",
      },
      {
        q: "will another agency's shoot be running in parallel?",
        a: "no — we're a single-hire studio. every booking is exclusive by default. no shared space, no other clients or crew coming through the door during your shoot day.",
      },
      {
        q: "who owns the images and rights on an agency-led shoot?",
        a: "you and your client own the imagery with full commercial usage rights. we retain the right to use imagery in our portfolio and social channels unless embargoed. happy to sign nda or usage agreements before shoot day.",
      },
      {
        q: "can you handle a full production including talent + wardrobe?",
        a: "yes — for agencies who want to offload the production layer, we can handle casting, wardrobe styling, mua, art direction and post end-to-end. get in touch with your brief and we'll come back with a full-production quote.",
      },
    ],
    cta: { label: "start a conversation", href: "/#contact" },
  },

  "photography-studio-east-london": {
    slug: "photography-studio-east-london",
    hidden: true,
    title: SEO_PAGES["/photography-studio-east-london"].title,
    description: SEO_PAGES["/photography-studio-east-london"].description,
    h1: "photography studio — east london",
    eyebrow: "east london · hackney wick · fish island",
    pricingLine: "from £50 per hour or £350 per day",
    keywords:
      "photography studio east london, photo studio hackney wick, daylight studio east london, studio hire hackney wick, photography studio fish island e3",
    heroImage:
      "https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/ctu9b0rb_PFS-037.jpg",
    heroImageAlt:
      "Daylight photography studio in East London — Peanut Factory Studio, Hackney Wick, Fish Island E3",
    quickAnswers: [
      "peanut factory studio is a 600 sq ft daylight photography studio in east london — on fish island, hackney wick, e3",
      "from £50 per hour or £350 per day — flexible half-day, full-day and multi-day rates seven days a week",
      "5 minutes' walk from hackney wick overground, 10 minutes from stratford, 20 minutes from shoreditch or liverpool street",
      "20+ original victorian skylights, three coloramas, hand painted backdrops, a prop library and a full kitchen",
      "east london's creative district — the fastest-growing hub for photographers, agencies and brand productions in the uk",
    ],
    intro:
      "if you're looking for a photography studio in east london, peanut factory studio is a 600 sq ft natural daylight space on fish island, hackney wick — postcode e3. we've been the go-to studio for east london's photographers, agencies, fashion brands and content producers since 2016. dropped into the heart of the queen elizabeth olympic park district, we're one of the best-located and best-lit studios anywhere in east london.",
    sections: [
      {
        h2: "where in east london is the studio",
        kind: "prose",
        body:
          "we're on fish island, hackney wick — a small canal-side neighbourhood in the london borough of hackney and tower hamlets, next to the queen elizabeth olympic park. postcode e3. fish island is the creative and industrial heart of east london — a mix of warehouse studios, galleries, breweries and independent restaurants, sitting between hackney wick overground station and the olympic park. the studio is a 5-minute walk from hackney wick station and a 10-minute walk from stratford international.",
      },
      {
        h2: "how east london creatives get to the studio",
        kind: "list",
        items: [
          "5 minutes' walk from hackney wick overground (london overground line)",
          "10 minutes from stratford (jubilee, central, elizabeth line, dlr, national rail)",
          "12 minutes from dalston kingsland on the overground",
          "15 minutes from shoreditch high street on the overground",
          "20 minutes from liverpool street via elizabeth line to stratford + short walk",
          "limited on-site loading available — most productions arrive by train",
        ],
      },
      {
        h2: "why east london is the right location for your shoot",
        kind: "prose",
        body:
          "east london has become the uk's most concentrated creative district — home to fashion labels, photographers, agencies, production companies and post houses. it's where a lot of the industry actually lives and works, which matters when you're putting a crew together. talent, stylists, muas and freelancers are almost all within 15 minutes' travel. rush-hour central-london gridlock isn't a factor. and the neighbourhood itself photographs beautifully — canal walks, warehouse walls, victorian brick — if your shoot needs a couple of environmental locations to complement the studio work, we're spoiled for options within a 5-minute radius.",
      },
      {
        h2: "what's included with every east london studio booking",
        kind: "list",
        items: [
          "600 sq ft of clear shooting space with 4m ceilings",
          "20+ original victorian sawtooth skylights (north-facing daylight)",
          "three coloramas and a range of hand painted backdrops",
          "prop library — folding chairs, tables, plants, vintage furniture",
          "kitchen with full appliances and makeup / changing area",
          "high-speed wi-fi and tethering points",
          "free bowens lighting kit with every full-day booking (10 hours)",
        ],
      },
      {
        h2: "east london studio rates",
        kind: "prose",
        body:
          "studio hire starts from £50 per hour or £350 per day — genuinely one of the best-value daylight studios in east london. full-day bookings (10 hours) include a free bowens lighting kit worth £150+. for multi-day shoots, brand campaigns and fashion-week productions we offer custom multi-day rates. drop us your dates and brief and we'll come back with a tailored quote within hours.",
      },
    ],
    faqs: [
      {
        q: "what's the postcode of the east london studio?",
        a: "e3 — we're on fish island, in the london borough of tower hamlets, right next to the border with hackney. 5 minutes' walk from hackney wick overground, 10 minutes from stratford international.",
      },
      {
        q: "is peanut factory studio in hackney or tower hamlets?",
        a: "technically we're just inside tower hamlets on fish island, but hackney wick sits right on the border and most people (including us) refer to the neighbourhood as hackney wick. the whole area is one connected creative district, so whether you say hackney or tower hamlets, you're in the same east london ecosystem.",
      },
      {
        q: "are there other east london studios nearby?",
        a: "yes — fish island and hackney wick are dense with photo, video, ceramics and art studios. we're one of the largest single-room natural daylight studios in the area, which is what tends to bring people to us for editorial and campaign work. we're happy to recommend other local spaces if you need something we don't offer (e.g. blackout, cyc-wall, larger volume).",
      },
      {
        q: "can i shoot on location in east london around the studio?",
        a: "yes — a lot of clients book us for a half-day studio shoot and combine it with a half-day of location work on the canal, in the olympic park, or around the fish island warehouses. we can help scout locations and, for full-production shoots, handle location permissions and logistics.",
      },
      {
        q: "how does east london compare to central or west london studios?",
        a: "east london studios like ours tend to be larger, better-lit and better-value than central london equivalents (where rents force studios into basements or infill spaces). the trade-off used to be transport, but the overground and elizabeth line have made east london one of the fastest-to-reach parts of the city — often quicker than fighting central-london traffic to west london studios in ladbroke grove or shepherd's bush.",
      },
    ],
    cta: { label: "book the east london studio", href: "/#contact" },
  },
};

/** Build the structured-data JSON-LD for a service page. */
export function buildServiceJsonLd(page) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${canonicalFor(`/${page.slug}`)}#service`,
    name: page.h1,
    description: page.description,
    serviceType: page.eyebrow,
    provider: { "@id": `${ORIGIN}/#business` },
    areaServed: [
      { "@type": "City", name: "London" },
      { "@type": "Place", name: "Hackney Wick" },
      { "@type": "Place", name: "Fish Island" },
      { "@type": "Place", name: "East London" },
    ],
    url: canonicalFor(`/${page.slug}`),
    mainEntityOfPage: canonicalFor(`/${page.slug}`),
    image: page.heroImage,
  };
}

/** Build a FAQPage JSON-LD for a service page. */
export function buildFaqJsonLd(page) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Build ImageObject schema for gallery images — boosts Google Images visibility. */
function buildGalleryImageJsonLd(page) {
  if (!page.gallery || page.gallery.length === 0) return null;
  const year = new Date().getUTCFullYear();
  return page.gallery.map((g, i) => ({
    "@type": "ImageObject",
    "@id": `${canonicalFor(`/${page.slug}`)}#img-${i}`,
    contentUrl: g.src,
    url: g.src,
    name: g.alt,
    description: g.alt,
    representativeOfPage: i === 0,
    creator: {
      "@type": "Organization",
      name: "Peanut Factory Studio",
      url: `${ORIGIN}/`,
    },
    copyrightNotice: `© ${year} Peanut Factory Studio`,
    copyrightHolder: {
      "@type": "Organization",
      name: "Peanut Factory Studio",
      url: `${ORIGIN}/`,
    },
    creditText: "Peanut Factory Studio",
    license: `${ORIGIN}/`,
    acquireLicensePage: `${ORIGIN}/#contact`,
  }));
}

/** Combined @graph wrapper so a single <script> covers Service + FAQ + Breadcrumbs. */
export function buildPageJsonLd(page) {
  const graph = [
    // WebPage entity — anchors this URL into the knowledge graph
    {
      "@type": "WebPage",
      "@id": `${canonicalFor(`/${page.slug}`)}#webpage`,
      url: canonicalFor(`/${page.slug}`),
      name: page.h1,
      description: page.description,
      isPartOf: { "@id": `${ORIGIN}/#website` },
      about: { "@id": `${ORIGIN}/#business` },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: page.heroImage,
        contentUrl: page.heroImage,
      },
      breadcrumb: { "@id": `${canonicalFor(`/${page.slug}`)}#breadcrumb` },
      inLanguage: "en-GB",
    },
    buildServiceJsonLd(page),
    buildFaqJsonLd(page),
    {
      "@type": "BreadcrumbList",
      "@id": `${canonicalFor(`/${page.slug}`)}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Services", item: `${ORIGIN}/#services` },
        { "@type": "ListItem", position: 3, name: page.h1, item: `${ORIGIN}/${page.slug}` },
      ],
    },
  ];

  const galleryImages = buildGalleryImageJsonLd(page);
  if (galleryImages) graph.push(...galleryImages);

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
